import React, { useEffect, useRef } from "react";
import { V2ModeContext } from "./V2ModeContext";
import { V2SearchProduct } from "./V2SearchProduct";
import {
  getSectionStatus,
  SECTION_IDS,
  resolveNavTarget,
} from "./sectionConfig";

import { useRequestContext } from "../hooks/useRequest";
import { SectionIcon } from "../../orderManagement/molecules/SectionTrigger";
import AccordionTrigger from "../../forms/SectionTrigger";
import SupplierSellRequestSlot from "./SupplierSellRequestSlot.jsx";
import { DetachRequestControl } from "./DetachRequestControl.jsx";

// Request-flow molecules
import { SearchProduct } from "../components/molecules/SearchProduct";
import { AssignClient } from "../components/molecules/AssignClient";
import { ConfirmRequest } from "../components/molecules/ConfirmRequest";
import { ConnectListing } from "../components/molecules/ConnectListing";
import { SendQuote } from "../components/molecules/SendQuote";
import { VerifySerial } from "../components/molecules/VerifySerial";
import { ConsignmentAgreement } from "../components/molecules/ConsignmentAgreement";
import { PurchasingAgreement } from "../components/molecules/PurchasingAgreement";
import { Payments as SellPayments } from "../components/molecules/Payments";
import { PurchaseOrder } from "../components/molecules/PurchaseOrder";
import { PublishListing } from "../components/molecules/PublishListing";

// Cart-flow molecules
import {
  AssignSellRequests,
  PxSummary,
  Payments as CartPayments,
  Invoice,
  Commissions,
  InternalDetailsAndCosts,
  TrackingNumber,
} from "../../orderManagement/molecules";
import { useOrderManagement } from "../../orderManagement/useOrderManagement";

// Hides the inner <Collapsible> trigger that each molecule renders.
// CSS selectors targeting these elements haven't been reliable due to
// build-cache issues, so we do it in JS after the DOM is painted.
function AccBody({ children }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const hide = () => {
      ref.current
        .querySelectorAll('[id^="collapsible-trigger"]')
        .forEach((el) => {
          el.style.display = "none";
        });
      // Force the content panel open (react-collapsible sets height/overflow
      // as inline styles that override any CSS we add).
      ref.current
        .querySelectorAll('.collapsible__contentOuter, .collapsible_contentOuter')
        .forEach((el) => {
          el.style.height = "auto";
          el.style.overflow = "visible";
        });
    };
    hide();
    // Re-run after a tick in case react-collapsible sets inline styles async.
    const t = setTimeout(hide, 50);
    return () => clearTimeout(t);
  });
  return <div ref={ref} className="rc-request-v2__acc-body">{children}</div>;
}

// Synthesize a journey-step object for request-flow molecules that
// expect {step, idx}. In V2 we force open=true since the middle pane
// already shows only one section at a time. pxSellIdx is non-null for
// the dynamic SELECT SELL REQUEST (m/n) sections so the molecules
// branch into the PX sub-flow.
function makeFakeStep(label, pxSellIdx = null, mainSlotIdx = null) {
  return {
    id: `v2-${label}`,
    label,
    open: true,
    hidden: false,
    component: null,
    pxSellIdx,
    // mainSlotIdx > 0 marks the additional MAIN slots (2nd / 3rd
    // products in a multi-main deal). useRequest.getContext reads
    // this to route the section to the right slot's state.
    mainSlotIdx,
  };
}

function CartContextProps() {
  const ctx = useOrderManagement();
  return ctx;
}

function MoleculeRouter({
  moleculeKey,
  defaultSource = null,
  label,
  pxSellIdx = null,
  mainSlotIdx = null,
  navigateTo,
  onAdvance,
  selectedQuote,
  onSelectQuote,
  isLastOrderQuote = false,
}) {
  const fakeStep = makeFakeStep(label, pxSellIdx, mainSlotIdx);

  // Request-flow molecules use useRequestContext internally; they just
  // need a step + idx prop pair.
  if (moleculeKey === "SearchProduct") {
    return (
      <V2SearchProduct
        onAdvance={onAdvance}
        pxSellIdx={pxSellIdx}
        mainSlotIdx={mainSlotIdx}
        defaultSource={defaultSource}
      />
    );
  }
  if (moleculeKey === "AssignClient") {
    return <AssignClient step={fakeStep} idx={0} onAdvance={onAdvance} />;
  }
  if (moleculeKey === "ConfirmRequest") {
    return (
      <ConfirmRequest
        step={fakeStep}
        idx={0}
        onAdvance={onAdvance}
        navigateTo={navigateTo}
      />
    );
  }
  if (moleculeKey === "ConnectListing") {
    return (
      <ConnectListing
        step={fakeStep}
        idx={0}
        selectedQuote={selectedQuote}
        onAdvance={onAdvance}
      />
    );
  }
  if (moleculeKey === "SendQuote") {
    return (
      <SendQuote
        step={fakeStep}
        idx={0}
        onAdvance={onAdvance}
        navigateTo={navigateTo}
        onSelectQuote={onSelectQuote}
        isLastOrderQuote={isLastOrderQuote}
      />
    );
  }
  if (moleculeKey === "VerifySerial") {
    return <VerifySerial step={fakeStep} idx={0} navigateTo={navigateTo} />;
  }
  if (moleculeKey === "ConsignmentAgreement") {
    return (
      <ConsignmentAgreement step={fakeStep} idx={0} navigateTo={navigateTo} />
    );
  }
  if (moleculeKey === "PurchasingAgreement") {
    return (
      <PurchasingAgreement step={fakeStep} idx={0} navigateTo={navigateTo} />
    );
  }
  if (moleculeKey === "SellPayments") {
    // Wrap in the cart-flow shell so V2's dark-theme styles for
    // currency/bank/fee/date-picker fields reach it. Without
    // .order-management-flow--inline as an ancestor, StepPayments
    // falls back to the global light styles (white field bg, white
    // text, invisible calendar day-names).
    return (
      <div className="order-management-flow order-management-flow--inline">
        <SellPayments step={fakeStep} idx={0} navigateTo={navigateTo} />
      </div>
    );
  }
  if (moleculeKey === "PurchaseOrder") {
    return <PurchaseOrder step={fakeStep} idx={0} navigateTo={navigateTo} />;
  }
  if (moleculeKey === "PublishListing") {
    return <PublishListing step={fakeStep} idx={0} />;
  }

  // Cart-flow molecules need all of useOrderManagement spread as props.
  return <CartMoleculeRouter moleculeKey={moleculeKey} navigateTo={navigateTo} />;
}

function CartMoleculeRouter({ moleculeKey, navigateTo }) {
  const ctx = useOrderManagement();
  // Read the parent Order Request + order from useRequestContext too —
  // some cart molecules (PxSummary) need them to resolve px_request_id
  // when calling px-request APIs. CartMoleculeRouter sits inside both
  // providers so both hooks are valid here.
  const {
    request: requestCtxRequest,
    order: requestCtxOrder,
    product: requestCtxProduct,
    additionalMainRequests: requestCtxAdditionalMainRequests,
    additionalMainProducts: requestCtxAdditionalMainProducts,
    confirmRequest: requestCtxConfirmRequest,
    mainData: requestCtxMainData,
    requestType: requestCtxRequestType,
    pxProducts: requestCtxPxProducts,
    journey: requestCtxJourney,
    // The SELECT SELL QUOTE sections write the picked quote into the REQUEST
    // context's map (useRequest.selectSlotQuote); PX SUMMARY's Live Calculation
    // reads the ORDER-MANAGEMENT map. Merged below so a pick in the flow moves
    // the banner immediately instead of waiting for a reload to re-hydrate OM.
    quotes: requestCtxQuotes,
  } = useRequestContext();
  if (!ctx) return null;

  const {
    step,
    lastStep,
    isPxOrder,
    client,
    listings,
    sellRequests,
    quotes,
    setQuotes,
    deposits,
    updateDepositField,
    taxRates,
    updateTaxRate,
    totalRemainingPaid,
    setTotalRemainingPaidField,
    paidAmount,
    populatedAmount,
    toPay,
    sellRequestsAmount,
    showPaymentErrors,
    arePaymentsValid,
    onRemoveSellRequest,
    proceedToPayment,
    savePayments,
    goToInvoice,
    getPdf,
    getPdfUrl,
    paymentDialog,
    updatePaymentDialog,
    goToCommissions,
    items,
    commissions,
    updateCommissionField,
    addCommissionSlot,
    removeCommissionSlot,
    itemProfit,
    saveCommissions,
    commissionsAmount,
    commissionFees,
    netProfitBeforeCommissions,
    skipSellRequests,
    costsCurrentListingIdx,
    fees,
    marketplaceFee,
    setMarketplaceFee,
    marketplaceFeeType,
    setMarketplaceFeeType,
    orderPaid,
    allPaymentsConfirmed,
    saveCosts,
    finaliseCosts,
    trackingNumber,
    setTrackingNumber,
    saveTrackingNumber,
    order_id,
    initToggleStatus,
    existUserCompany,
    goToSummary,
  } = ctx;

  // Cart molecules' SCSS (label-value-row flex, strong-label width,
  // inputs, etc.) lives under .order-management-flow — wrap so V2
  // inherits the same layout used by the V1 modal.
  const renderCart = (node) => (
    <div className="order-management-flow order-management-flow--inline">
      {node}
    </div>
  );

  switch (moleculeKey) {
    case "AssignSellRequests":
      return renderCart(
        <AssignSellRequests
          {...{
            step,
            lastStep,
            isPxOrder,
            client,
            sellRequests,
            skipSellRequests,
            goToSummary,
          }}
        />
      );
    case "PxSummary":
      return renderCart(
        <PxSummary
          {...{
            step,
            lastStep,
            client,
            // V2 doesn't otherwise pass the parent Order Request to
            // cart-flow molecules; pull from request context. PxSummary
            // needs `request` to resolve `px_request_id` when calling
            // /api/px-requests/{id}/quotes — without it the CHANGE
            // QUOTE selections silently fail to persist.
            request: requestCtxRequest,
            order: requestCtxOrder,
            // Multi-main draft preview: when the rep clicks FINALISE
            // PART EXCHANGE on a fresh PX (no quotes/listings yet),
            // listings is [] and SummaryContent's LISTING tile is
            // empty. Feed the primary + additional main products /
            // requests so PxSummaryContent can show every order
            // product as a draft row until listings hydrate.
            primaryProduct: requestCtxProduct,
            additionalMainRequests: requestCtxAdditionalMainRequests,
            additionalMainProducts: requestCtxAdditionalMainProducts,
            listings,
            sellRequests,
            // Request-context selections win: they are the ones the rep just
            // made in a SELECT QUOTE section. OM's map is the persisted /
            // re-hydrated baseline underneath.
            quotes: { ...(quotes ?? {}), ...(requestCtxQuotes ?? {}) },
            setQuotes,
            isPxOrder,
            onRemoveSellRequest,
            proceedToPayment,
            // PX SUMMARY is the FINAL review step (the rep has already
            // walked the SELECT QUOTE block, so the summary shows a
            // complete net). Proceed advances to INTERNAL DETAILS &
            // COSTS, which now precedes PAYMENTS so the serial is
            // recorded before the invoice that quotes it. A deal with no
            // linked listing has no such section — resolveNavTarget
            // carries the hand-over on to PAYMENTS.
            onProceed: navigateTo ? () => navigateTo("internal_details") : null,
            navigateTo,
            confirmRequest: requestCtxConfirmRequest,
            mainData: requestCtxMainData,
            requestType: requestCtxRequestType,
            pxProducts: requestCtxPxProducts,
            journey: requestCtxJourney,
          }}
        />
      );
    case "Payments":
      return renderCart(
        <CartPayments
          {...{
            step,
            lastStep,
            isPxOrder,
            client,
            listings,
            sellRequests,
            quotes,
            deposits,
            updateDepositField,
            taxRates,
            updateTaxRate,
            totalRemainingPaid,
            showPaymentErrors,
            setTotalRemainingPaidField,
            arePaymentsValid,
            savePayments,
            goToInvoice,
            paymentDialog,
            updatePaymentDialog,
            paidAmount,
            // populatedAmount drives the bottom action button's label
            // (Add to cart / Save deposits / Save Payments / Mark as
            // Paid). Without it the V2 inline Payments section always
            // falls through to "Add to cart" because !undefined is
            // truthy — regression introduced when STRIPE LINK Path A
            // split paidAmount (collected money) from populatedAmount
            // (any populated row including drafts) and the modal-mode
            // Payments wiring was updated but the V2 prop spread here
            // was missed.
            populatedAmount,
            toPay,
            orderPaid,
            fees,
            sellRequestsAmount,
            allPaymentsConfirmed,
            marketplaceFee,
            setMarketplaceFee,
            marketplaceFeeType,
            setMarketplaceFeeType,
          }}
        />
      );
    case "Invoice":
      return renderCart(
        <Invoice
          {...{
            step,
            order_id,
            getPdf,
            getPdfUrl,
            lastStep,
            goToCommissions,
            initToggleStatus,
            existUserCompany,
          }}
        />
      );
    case "Commissions":
      return renderCart(
        <Commissions
          {...{
            step,
            lastStep,
            commissions,
            updateCommissionField,
            addCommissionSlot,
            removeCommissionSlot,
            itemProfit,
            saveCommissions,
            items,
            netProfitBeforeCommissions,
            commissionsAmount,
            isPxOrder,
          }}
        />
      );
    case "InternalDetailsAndCosts":
      return renderCart(
        <>
          {(listings || []).map((listing, idx) => {
            if (idx > costsCurrentListingIdx) return null;
            return (
              <InternalDetailsAndCosts
                key={listing.id}
                {...{
                  order_id: items && items[0]?.order_id,
                  step,
                  lastStep,
                  listings,
                  fees,
                  commissionsAmount,
                  commissionFees,
                  marketplaceFee,
                  setMarketplaceFee,
                  idx,
                  costsCurrentListingIdx,
                  saveCosts,
                  finaliseCosts,
                  // Every order/px INTERNAL DETAILS & COSTS links a supplier
                  // SELL REQUEST here instead of picking a bare supplier — the
                  // record of how the store acquired the watch. Previously
                  // gated on `flowVariant`, so the canonical /request/order/:id
                  // URL (where flowVariant is null) fell back to the raw
                  // supplier dropdown and the slot never appeared. This section
                  // only renders for order/px flows, so it is always the right
                  // control here.
                  supplierSlot: (l, locked) => (
                    <SupplierSellRequestSlot
                      key={l?.id ?? "unsaved"}
                      listing={l}
                      readOnly={!!locked}
                    />
                  ),
                }}
              />
            );
          })}
        </>
      );
    case "TrackingNumber":
      return renderCart(
        <TrackingNumber
          {...{
            step,
            lastStep,
            isPxOrder,
            paidAmount,
            trackingNumber,
            setTrackingNumber,
            saveTrackingNumber,
          }}
        />
      );
    default:
      return null;
  }
}

export function RequestSectionView({
  section,
  sections = [],
  onSelect,
  selectedQuote,
  onSelectQuote,
  state,
}) {
  const selectedId = section?.id;

  // A navigation target queued by navigateTo below because it was not in this
  // render's section list — ADD / ADD_MAIN navigate to the section whose
  // journey entry they have only just pushed. Selected on the first render
  // where it exists; abandoned untouched if it never materialises.
  const pendingNavRef = useRef(null);
  const sectionsKey = sections.map((s) => s.id).join(",");
  useEffect(() => {
    const target = pendingNavRef.current;
    if (!target) return;
    if (sections.some((s) => s.id === target)) {
      pendingNavRef.current = null;
      onSelect(target);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionsKey]);

  // Order-side quote sections, in flow order: the primary SEND_QUOTE plus any
  // per-slot ones. Sell-side quotes (send_quote_sell_n) are deliberately NOT
  // in here — they are a separate block that runs after these.
  const orderQuoteIds = sections
    .filter(
      (s) =>
        s.id === SECTION_IDS.SEND_QUOTE ||
        String(s.id).startsWith("send_quote_main_")
    )
    .map((s) => s.id);

  return (
    <V2ModeContext.Provider value={true}>
    <div className="rc-request-v2__accordion">
      {sections.map((s, idx) => {
        const isOpen = s.id === selectedId;
        // Advance lands on the next section that still needs the rep, not
        // blindly on idx+1: record-sale hoists ASSIGN CLIENT in front of an
        // already-filled SEARCH PRODUCT, and stopping there made assigning a
        // client feel like it went nowhere. Completeness is read from the
        // render-time state, which is exactly right — a section complete
        // BEFORE this advance is the one worth skipping. Locked sections are
        // never skipped (the lock usually clears with the write that triggered
        // the advance), and the immediate neighbour is the fallback.
        const next =
          sections.slice(idx + 1).find((s2) => !(state && getSectionStatus(s2, state).kind === "complete")) ??
          (idx < sections.length - 1 ? sections[idx + 1] : null);
        const status = state ? getSectionStatus(s, state) : null;
        const isLocked = status?.kind === "locked";
        return (
          <div
            key={s.id}
            className={`rc-request-v2__acc-item${isOpen ? " rc-request-v2__acc-item--open" : ""}${isLocked ? " rc-request-v2__acc-item--locked" : ""}`}
          >
            <AccordionTrigger
              label={s.label}
              icon={<SectionIcon icon={s.icon} />}
              isOpen={isOpen}
              onToggle={() => onSelect(s.id)}
            />
            {isOpen && (
              isLocked ? (
                <div className="rc-request-v2__acc-locked">
                  {status.message || "Complete the previous step first"}
                </div>
              ) : (
                <AccBody>
                  <MoleculeRouter
                    moleculeKey={s.moleculeKey}
                    label={s.label}
                    pxSellIdx={s.pxSellIdx ?? null}
                    mainSlotIdx={s.mainSlotIdx ?? null}
                    defaultSource={s.defaultSource ?? null}
                    // navigateTo lets molecules jump to any section by
                    // id. ConfirmRequest uses it after ADD / ADD_MAIN
                    // to land on the freshly-inserted SEARCH (m/n)
                    // section, instead of relying on the stale `next`
                    // captured at the previous render.
                    navigateTo={(targetId) => {
                      // Two kinds of miss, two treatments. A target that can
                      // NEVER exist on this deal (an Active-Listings order has
                      // no quote section) resolves forward to its successor.
                      // A target that does not exist YET — ADD / ADD_MAIN have
                      // just pushed its journey entry, and this render's list
                      // predates that — is queued, and the effect below selects
                      // it on the first render where it appears. Dropping it
                      // (the old behaviour) stranded the rep on whatever the
                      // shell fell back to — on a PX conversion, the locked
                      // SHOPPING CART section.
                      const resolved = resolveNavTarget(sections, targetId);
                      if (resolved) {
                        onSelect(resolved);
                      } else {
                        pendingNavRef.current = targetId;
                      }
                    }}
                    onAdvance={(action) => {
                      // ADD / ADD_MAIN insert a fresh SEARCH section.
                      // ConfirmRequest navigates to it explicitly via
                      // navigateTo; this path skips the captured-next
                      // advance to avoid jumping past the new slot.
                      if (action === "add" || action === "add_main") {
                        return;
                      }
                      next && onSelect(next.id);
                    }}
                    selectedQuote={selectedQuote}
                    onSelectQuote={onSelectQuote}
                    // Whether this is the LAST order-quote section that exists.
                    // Counting additionalMainProducts would be wrong now: a slot
                    // picked from Active Listings has no quote section at all, so
                    // the last VISIBLE one is what should hand over to the sell
                    // quotes / payments.
                    isLastOrderQuote={
                      orderQuoteIds.length > 0 &&
                      orderQuoteIds[orderQuoteIds.length - 1] === s.id
                    }
                  />
                  {/* Per-slot "take this product out of the deal". Declared by
                      the section (only the additional main slots and the
                      trade-in slots), never the primary. */}
                  {s.detachable && (
                    <DetachRequestControl
                      pxSellIdx={s.pxSellIdx ?? null}
                      mainSlotIdx={s.mainSlotIdx ?? null}
                      label={s.label}
                    />
                  )}
                </AccBody>
              )
            )}
          </div>
        );
      })}
    </div>
    </V2ModeContext.Provider>
  );
}
